/**
 * Phase 45 Plan 45-08 (D-16) — task-level attachments hook.
 *
 * Wraps the three Plan 45-04 attachment endpoints. Upload uses multipart
 * server-side flow per the 45-04 Deviation #1 (no presigned-PUT in the
 * codebase — fileUploadService uses uploadFile + getDownloadUrl).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type TaskAttachmentDto,
  fetchTaskAttachments,
  uploadTaskAttachment,
  deleteTaskAttachment,
} from '../services/api/taskAttachments';
import { ApiClientError } from '../services/api/client';
import { useToast } from '../components/ui/Toast';

export const taskAttachmentsQueryKey = (taskId: number) =>
  ['tasks', taskId, 'attachments'] as const;

function statusOf(err: unknown): number | undefined {
  if (err instanceof ApiClientError) return err.statusCode;
  if (err && typeof err === 'object' && 'statusCode' in err) {
    const s = (err as { statusCode?: unknown }).statusCode;
    return typeof s === 'number' ? s : undefined;
  }
  return undefined;
}

function attachmentErrorMessage(err: unknown): string {
  if (err instanceof ApiClientError) {
    const details = (err.details ?? null) as { code?: string } | null;
    const code = details?.code;
    if (code === 'ATTACHMENT_TOO_LARGE') return 'Attachment is too large (25 MB cap).';
    if (code === 'ATTACHMENT_DISALLOWED_TYPE') return 'Attachment type not allowed.';
    if (code === 'ATTACHMENT_TOO_MANY') return 'This task already has 10 attachments.';
    if (err.statusCode === 403) return 'Not allowed to manage attachments on this task.';
    return err.message || 'Upload failed.';
  }
  return 'Upload failed.';
}

export function useTaskAttachments(taskId: number | null) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const query = useQuery<TaskAttachmentDto[]>({
    queryKey:
      taskId != null
        ? taskAttachmentsQueryKey(taskId)
        : ['tasks', 'noop', 'attachments'],
    queryFn: () => fetchTaskAttachments(taskId as number),
    enabled: taskId != null,
    staleTime: 30_000,
  });

  function invalidate() {
    if (taskId == null) return;
    queryClient.invalidateQueries({ queryKey: taskAttachmentsQueryKey(taskId) });
  }

  const uploadAttachment = useMutation<TaskAttachmentDto, Error, File>({
    mutationFn: (file) => uploadTaskAttachment(taskId as number, file),
    onError: (err) => {
      toast(attachmentErrorMessage(err), 'error');
    },
    onSettled: () => invalidate(),
  });

  const deleteAttachment = useMutation<void, Error, number>({
    mutationFn: (attachmentId) =>
      deleteTaskAttachment(taskId as number, attachmentId),
    onError: (err) => {
      const status = statusOf(err);
      if (status === 403) {
        toast('Only the uploader or an admin can delete this attachment.', 'error');
      } else {
        toast("Couldn't delete attachment.", 'error');
      }
    },
    onSettled: () => invalidate(),
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    uploadAttachment,
    deleteAttachment,
  };
}

export type { TaskAttachmentDto };
